import { Component, OnDestroy, OnInit, inject } from '@angular/core';

import { IonApp, IonRouterOutlet, Platform, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonSpinner, ToastController } from '@ionic/angular';
import { Router, NavigationEnd } from '@angular/router';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { firstValueFrom, from, Subject, Subscription } from 'rxjs';
import { ChatToggleComponent } from '../components/chat-toggle/chat-toggle.component';
import { ChatOverlayComponent } from '../components/chat-overlay/chat-overlay.component';
import { SoloChatOverlayComponent } from '../components/solo-chat/solo-chat-overlay.component';
import { ChatService, ChatOverlayMode, ChatSurface } from '../services/chat.service';
import { NavigationHistoryService } from '../services/navigation-history.service';
import { AuthService } from '../services/auth.service';
import { PresentationService, PresentationState } from '../services/presentation.service';
import { AppConfigService } from '../services/app-config.service';
import { ConfigService } from '../services/config.service';
import { LoggerService } from '../services/logger.service';
import { ThemeService } from '../services/theme.service';
import { ProfileData, ProfileService } from '../services/profile.service';
import { AppSettingsService } from '../services/app-settings.service';
import { DiagnosticsSessionService } from '../services/diagnostics-session.service';
import { LogCollectorService } from '../services/log-collector.service';
import { registerIcons } from '../utils/icon-registry';
import { ImpersonationBannerComponent } from '../components/impersonation-banner/impersonation-banner.component';
import { SupportMemberBannerComponent } from '../components/support-member-banner/support-member-banner.component';
import { ShellTopBannersComponent } from '../components/shell-top-banners/shell-top-banners.component';
import { EnforcementBannerComponent } from '../components/billing/enforcement/enforcement-banner.component';
import { BillingSidebarCreditsGaugeComponent } from '../components/billing/billing-sidebar-credits-gauge.component';
import { IBillingMockService } from '../services/billing-mock/billing-mock.interface';
import { BILLING_SERVICE } from '../services/billing-mock/billing.token';
import { AICreditToastDispatcherService } from '../services/ai-credit-toast-dispatcher.service';
import { BuildStampComponent } from './components/build-stamp/build-stamp.component';
import { CurrencyInputLocaleService } from '../services/currency-input-locale.service';
import { FirstRunThemePromptComponent } from '../components/first-run-theme-prompt/first-run-theme-prompt.component';
import { PasskeyPromptComponent } from '../components/passkey-prompt/passkey-prompt.component';
import { TranslationService } from '../services/translation.service';
import { LanguagePreviewAccessService } from '../i18n/language-preview-access.service';

/** Solo brand default accent (deep green). */
export const DEFAULT_ACCENT_COLOR = '#0E5B3A';

/** Retired default accents (lowercase) migrated to DEFAULT_ACCENT_COLOR on load. */
const LEGACY_DEFAULT_ACCENT_COLORS = ['#7c3aed', '#8b5cf6'];

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    imports: [
    IonApp,
    IonRouterOutlet,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonSpinner,
    ChatToggleComponent,
    ChatOverlayComponent,
    SoloChatOverlayComponent,
    ImpersonationBannerComponent,
    SupportMemberBannerComponent,
    ShellTopBannersComponent,
    EnforcementBannerComponent,
    BillingSidebarCreditsGaugeComponent,
    BuildStampComponent,
    FirstRunThemePromptComponent,
    PasskeyPromptComponent
]
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private platform = inject(Platform);
  private chatService = inject(ChatService);
  private authService = inject(AuthService);
  private presentationService = inject(PresentationService);
  private appConfigService = inject(AppConfigService);
  private configService = inject(ConfigService);
  private logger = inject(LoggerService);
  private themeService = inject(ThemeService);
  private profileService = inject(ProfileService);
  private appSettingsService = inject(AppSettingsService);
  private diagnosticsSession = inject(DiagnosticsSessionService);
  private logCollector = inject(LogCollectorService);
  private diagnosticsSessionSubscription?: Subscription;
  private toastController = inject(ToastController);
  // Wired only for the #06 post-recovery toast. paymentRecoveryEvents$ lives on
  // the IBillingMockService interface; the real billing service will expose it
  // as a no-op observable when wired.
  private billingForRecovery = inject<IBillingMockService>(BILLING_SERVICE);
  // #08: low-balance AI credit toast dispatcher. Subscribes to the billing
  // service's AICreditBalance stream and fires once-per-cycle threshold toasts.
  private aiCreditToastDispatcher = inject(AICreditToastDispatcherService);
  private currencyInputLocaleService = inject(CurrencyInputLocaleService);
  private translationService = inject(TranslationService);
  private languagePreviewAccess = inject(LanguagePreviewAccessService);
  // Injected at bootstrap so the chat context trail records from app start.
  private navigationHistory = inject(NavigationHistoryService);
  private readonly destroy$ = new Subject<void>();
  private currentUrl = '';
  private firstRunThemePromptHandled = false;
  private activeThemeMode = 'auto';
  private activeAccentColor = DEFAULT_ACCENT_COLOR;

  showChatToggle = false;
  showChatPanel = false;
  chatOverlayMode: ChatOverlayMode = 'panel';
  /**
   * Which chat the panel renders (Channels C0b, C0B-DESIGN 3.7): the Solo panel where the surface is `agents` (dev),
   * the ChatKit panel where it is `chatkit` (test and production until C8); null while it is read for this sign-in.
   */
  chatSurface: ChatSurface | null = null;
  isDesktop = false;
  presentationState: PresentationState = { mode: 'default' };
  isDiagnosticCaptureEnabled = false;
  isDiagnosticCaptureActive = false;
  isDiagnosticCaptureBusy = false;
  diagnosticSessionId: string | null = null;
  diagnosticSessionError: string | null = null;
  isDiagnosticBannerFadingOut = false;
  isDiagnosticBannerVisible = false;
  diagnosticCopiedConfirmation = false;
  showFirstRunThemePrompt = false;
  private activityHandler: ((event: Event) => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor() {
    // Register all app icons centrally
    registerIcons();
    
    // Handle page visibility changes to refresh cache
    this.visibilityHandler = () => {
      try {
        if (!document.hidden) {
          // App is visible again, check if cache should be cleared
          const lastVisible = localStorage.getItem('last-visible');
          if (lastVisible) {
            const timeDiff = Date.now() - parseInt(lastVisible);
            // Clear cache if app was hidden for more than 30 minutes
            if (timeDiff > 30 * 60 * 1000) {
              this.appConfigService.clearCache();
              this.logger.debug('AppComponent', 'visibilitychange', 'Cache cleared after extended background period');
            }
          }
          // Check if auth session changed while tab was hidden (safety net for missed storage events)
          this.authService.checkAndSyncSession();
          this.authService.startInactivityTimeoutTracking();
        } else {
          // App is hidden, store timestamp
          localStorage.setItem('last-visible', Date.now().toString());
        }
      } catch (error) {
        // localStorage may be unavailable in private browsing mode
        this.logger.error('AppComponent', 'visibilitychange', 'Failed to access localStorage', { error });
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  async ngOnInit() {
    this.setupLocalizationSubscription();
    // Apply theme early to avoid flash of unstyled content
    await this.initializeTheme();
    await this.ensureProfileLoaded();
    await this.maybeShowFirstRunThemePrompt();

    this.checkAndClearCacheOnUpdate();
    this.setupPlatformDetection();
    this.setupRouterSubscription();
    this.setupFirstRunThemePromptSubscription();
    this.setupSystemThemeSubscription();
    this.setupChatSubscription();
    this.setupPresentationSubscription();
    this.setupPaymentRecoveryToast();
    this.aiCreditToastDispatcher.start();
    await this.setupDiagnosticCapture();
    this.registerActivityListeners();
    this.authService.startActivityBasedRefresh();
    this.authService.startInactivityTimeoutTracking();
  }

  ngOnDestroy(): void {
    if (this.diagnosticsSessionSubscription) {
      this.diagnosticsSessionSubscription.unsubscribe();
    }
    this.removeActivityListeners();
    this.removeVisibilityListener();
    this.authService.stopActivityBasedRefresh();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * #06: Post-recovery toast. Fires whenever __simulatePaymentRecovery() is
   * invoked (typically from the dev toolbar). Suppressed when the user is on
   * /billing since the hub page already shows the recovered state.
   */
  private setupPaymentRecoveryToast(): void {
    this.billingForRecovery.paymentRecoveryEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        if (this.currentUrl.startsWith('/billing')) {
          this.logger.info(
            'AppComponent',
            'setupPaymentRecoveryToast',
            'suppressing post-recovery toast - user is on /billing'
          );
          return;
        }
        const toast = await this.toastController.create({
          message: 'Payment received - full access restored.',
          duration: 5000,
          color: 'success',
          position: 'bottom'
        });
        await toast.present();
        this.logger.info(
          'AppComponent',
          'setupPaymentRecoveryToast',
          'post-recovery toast presented'
        );
      });
  }

  async setupDiagnosticCapture(): Promise<void> {
    try {
      const settings = await this.appSettingsService.loadSettings();
      const isEnabled = settings.features?.['diagnosticCapture'] ?? true;

      this.isDiagnosticCaptureEnabled = isEnabled;
      this.diagnosticsSession.initialize(isEnabled);
      this.logCollector.setDiagnosticSessionId(this.diagnosticsSession.activeSessionId);

      this.diagnosticsSessionSubscription = this.diagnosticsSession.state$.subscribe(state => {
        this.isDiagnosticCaptureActive = state.isActive;
        this.isDiagnosticCaptureBusy = state.isBusy;
        this.diagnosticSessionError = state.error;
        this.diagnosticSessionId = state.sessionId;
        this.isDiagnosticBannerVisible = state.isActive || state.isBusy || !!state.error;
        this.logCollector.setDiagnosticSessionId(state.sessionId);
      });
    } catch (error) {
      this.logger.error('AppComponent', 'setupDiagnosticCapture', 'Failed to initialize diagnostic capture', { error });
      this.isDiagnosticCaptureEnabled = false;
      this.diagnosticsSession.initialize(false);
    }
  }

  async toggleDiagnosticCapture(): Promise<void> {
    if (!this.isDiagnosticCaptureEnabled) {
      return;
    }

    if (this.isDiagnosticCaptureActive) {
      const sessionId = this.diagnosticSessionId;
      await this.diagnosticsSession.stopCapture('Stopped from Solo UI.');

      if (sessionId) {
        await this.copyToClipboard(sessionId);
        this.diagnosticCopiedConfirmation = true;
        this.diagnosticSessionId = sessionId;
        this.isDiagnosticBannerVisible = true;
      }

      setTimeout(() => {
        this.isDiagnosticBannerFadingOut = true;
        setTimeout(() => {
          this.isDiagnosticBannerVisible = false;
          this.isDiagnosticBannerFadingOut = false;
          this.diagnosticCopiedConfirmation = false;
          this.diagnosticSessionId = null;
        }, 500);
      }, 2500);
    } else {
      this.isDiagnosticBannerFadingOut = false;
      this.diagnosticCopiedConfirmation = false;
      await this.diagnosticsSession.startCapture('Started from Solo UI.');
    }
  }

  private async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }
  
  private checkAndClearCacheOnUpdate(): void {
    try {
      const VERSION_KEY = 'app-version';
      const settings = this.appSettingsService.getSettings();
      const currentVersion = [
        settings.build?.productVersion,
        settings.build?.buildStamp,
        settings.build?.shortSha
      ].filter(Boolean).join(':') || 'local';
      const storedVersion = localStorage.getItem(VERSION_KEY);
      
      if (storedVersion !== currentVersion) {
        // New version detected, clear all caches
        this.appConfigService.clearCache();
        this.configService.clearCache();
        localStorage.setItem(VERSION_KEY, currentVersion);
        this.logger.debug('AppComponent', 'checkAndClearCacheOnUpdate', 'App updated: Cache cleared');
      }
    } catch (error) {
      // localStorage may be unavailable in private browsing mode
      this.logger.error('AppComponent', 'checkAndClearCacheOnUpdate', 'Failed to access localStorage', { error });
      // Still clear cache even if we can't store the version
      this.appConfigService.clearCache();
      this.configService.clearCache();
    }
  }

  private setupPlatformDetection(): void {
    this.isDesktop = this.platform.is('desktop');
  }

  private setupRouterSubscription(): void {
    this.currentUrl = this.router.url;
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(event => {
      this.currentUrl = event.urlAfterRedirects ?? event.url;
      this.updateChatToggleVisibility();
    });

    // On a hard page load the initial NavigationEnd fires before the stored
    // session has been applied, so also recompute whenever auth state changes
    // (session load, login, logout) — otherwise the chat toggle stays hidden
    // until the next SPA navigation.
    this.authService.authSession$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.updateChatToggleVisibility());
  }

  private updateChatToggleVisibility(): void {
    // The floating toggle is the single entry point to chat, so it shows on
    // every authenticated page. Excluded: auth/signup flows (no session
    // chrome) and the /apps launcher tile grid, which stays chrome-free.
    const excludedPrefixes = ['/login', '/verify', '/signup', '/auth', '/dev-auth', '/d/'];
    const path = this.currentUrl.split(/[?#]/)[0];
    this.showChatToggle = !!this.authService.getAuthToken() &&
      path !== '/apps' &&
      !excludedPrefixes.some(prefix => path.startsWith(prefix));
  }

  private setupChatSubscription(): void {
    // The unified overlay is hosted here for every form factor; CSS decides
    // whether it renders as a sidebar, expanded overlay, or fullscreen.
    this.chatService.isOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOpen => {
        this.showChatPanel = isOpen;
      });

    this.chatService.overlayMode
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        this.chatOverlayMode = mode;
      });

    // Read once per sign-in by ChatService; every entry point opens whichever panel it names.
    this.chatService.surface$
      .pipe(takeUntil(this.destroy$))
      .subscribe(surface => {
        this.chatSurface = surface;
      });
  }

  closeChatOverlay(): void {
    this.chatService.closeChat();
  }

  private setupPresentationSubscription(): void {
    this.presentationService.presentationState$.subscribe(state => {
      this.presentationState = state;
    });
  }

  /**
   * Initializes theme with localStorage-first approach
   * 1. Check localStorage first (fastest, no backend call)
   * 2. If not found and authenticated, load from backend and save to localStorage
   * 3. Otherwise apply default theme
   * - Error fallback: Apply the default System theme
   * Stored accents equal to a retired default (the original purple) are
   * migrated to the current brand default so old sessions pick up the rebrand
   * while user-chosen custom accents are preserved.
   */
  private async initializeTheme(): Promise<void> {
    try {
      // STEP 1: Try loading from localStorage first (fastest path)
      const localTheme = await this.themeService.loadThemeFromStorage();

      if (localTheme) {
        // Theme found in localStorage - apply immediately and exit
        const accentColor = this.normalizeAccentColor(localTheme.accentColor);
        this.logger.debug('AppComponent', 'initializeTheme', 'Theme loaded from localStorage', {
          themeMode: localTheme.mode,
          accentColor
        });
        this.logger.info('AppComponent', 'initializeTheme', 'Theme source: localStorage');
        this.applyTheme(localTheme.mode, accentColor);
        if (accentColor !== localTheme.accentColor) {
          await this.themeService.saveThemeToStorage(localTheme.mode, accentColor);
        }
        return;
      }

      // STEP 2: localStorage empty - check authentication
      const isAuthenticated = this.authService.isAuthenticatedSync();

      if (isAuthenticated) {
        // Load profile from backend and save to localStorage for next time
        try {
          const profile = await firstValueFrom(this.profileService.getProfile());
          const themeMode = profile?.HasSelectedTheme === false
            ? 'auto'
            : profile?.ThemeMode || 'auto';
          const accentColor = this.normalizeAccentColor(profile?.AccentColor || DEFAULT_ACCENT_COLOR);

          this.logger.debug('AppComponent', 'initializeTheme', 'Theme loaded from backend and saved to localStorage', {
            themeMode,
            accentColor
          });
          this.logger.info('AppComponent', 'initializeTheme', 'Theme source: backend');

          // Apply theme and save to localStorage for future loads
          this.applyTheme(themeMode, accentColor);
          await this.themeService.saveThemeToStorage(themeMode, accentColor);
        } catch (profileError) {
          // If profile loading fails, apply default theme
          this.logger.error('AppComponent', 'initializeTheme', 'Failed to load profile, applying default theme', {
            error: profileError
          });
          this.logger.info('AppComponent', 'initializeTheme', 'Theme source: default (error fallback)');
          this.applyTheme('auto', DEFAULT_ACCENT_COLOR);
        }
      } else {
        // Apply default theme for unauthenticated users
        this.logger.debug('AppComponent', 'initializeTheme', 'User not authenticated, applying default theme');
        this.logger.info('AppComponent', 'initializeTheme', 'Theme source: default (unauthenticated)');
        this.applyTheme('auto', DEFAULT_ACCENT_COLOR);
      }
    } catch (error) {
      // Fallback to default theme on any error
      this.logger.error('AppComponent', 'initializeTheme', 'Error initializing theme, applying default', {
        error
      });
      this.logger.info('AppComponent', 'initializeTheme', 'Theme source: default (error fallback)');
      this.applyTheme('auto', DEFAULT_ACCENT_COLOR);
    }
  }

  /**
   * Keeps the app's current theme metadata in sync with every shell-level
   * application. The OS preference listener uses this as its no-storage
   * fallback (for example, on logged-out routes).
   */
  private applyTheme(mode: string, accentColor: string): void {
    this.activeThemeMode = mode;
    this.activeAccentColor = accentColor;
    this.themeService.applyTheme(mode, accentColor);
  }

  /**
   * Maps retired default accents (the pre-rebrand purple) to the current
   * brand default. Custom user accents pass through untouched.
   */
  private normalizeAccentColor(accentColor: string): string {
    return LEGACY_DEFAULT_ACCENT_COLORS.includes(accentColor.toLowerCase())
      ? DEFAULT_ACCENT_COLOR
      : accentColor;
  }

  /**
   * Hydrates ProfileService on authenticated startup even when theme settings
   * were satisfied from local storage. Date/time formatting depends on profile
   * preferences, so theme cache must not short-circuit profile availability.
   */
  private async ensureProfileLoaded(): Promise<void> {
    if (!this.authService.isAuthenticatedSync() || this.profileService.getCurrentProfile()) {
      return;
    }

    try {
      await firstValueFrom(this.profileService.getProfile());
    } catch (error) {
      this.logger.error('AppComponent', 'ensureProfileLoaded', 'Failed to hydrate profile on startup', { error });
    }
  }

  /**
   * Applies the effective per-tenant language whenever profile hydration runs.
   * Account switching clears and reloads ProfileService, so the active tenant
   * context changes without another login or a full-page reload.
   */
  private setupLocalizationSubscription(): void {
    this.profileService.profile$
      .pipe(
        switchMap(profile => from(this.applyProfileLanguage(profile))),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * Reads whether this account is a language tester before applying its language, so a stored
   * preview language renders only for testers (AB#19412). Never writes a stored preference.
   */
  private async applyProfileLanguage(profile: ProfileData | null): Promise<void> {
    if (profile) {
      this.currencyInputLocaleService.setEffectiveLocaleCode(profile.EffectiveLocaleCode);
    }

    try {
      await this.languagePreviewAccess.refresh(profile !== null);
    } catch (error) {
      this.logger.error('AppComponent', 'applyProfileLanguage', 'Failed to read the language preview permission', { error });
    }

    // Signed out: a preview language a tester was using is no longer offered, so it re-resolves to English.
    const languageCode = profile
      ? profile.EffectiveLanguageCode || 'en'
      : this.translationService.getCurrentLanguage().code;
    if (!profile && this.isOffered(languageCode)) {
      return;
    }

    try {
      await this.translationService.setLanguage(languageCode);
    } catch (error) {
      this.logger.error(
        'AppComponent',
        'setupLocalizationSubscription',
        'Failed to apply the effective tenant language',
        { error, languageCode }
      );
    }
  }

  private isOffered(languageCode: string): boolean {
    const base = languageCode.split('-')[0].toLowerCase();
    return this.translationService.languages.some(language => language.code === base);
  }

  /**
   * Shows onboarding only when the backend explicitly identifies an
   * authenticated profile as undecided. Missing/older fields are deliberately
   * treated as decided so an API rollout cannot disrupt existing themes.
   */
  private async maybeShowFirstRunThemePrompt(): Promise<void> {
    if (
      this.firstRunThemePromptHandled ||
      this.showFirstRunThemePrompt ||
      !this.authService.isAuthenticatedSync()
    ) {
      return;
    }

    const profile = this.profileService.getCurrentProfile();
    if (profile?.HasSelectedTheme !== false) {
      return;
    }

    const accentColor = this.normalizeAccentColor(profile.AccentColor || DEFAULT_ACCENT_COLOR);

    // The backend's explicit undecided signal is authoritative over stale
    // device-local preferences. Persist auto locally so OS-change handling and
    // a refresh before the prompt is answered continue to use System.
    this.applyTheme('auto', accentColor);
    this.showFirstRunThemePrompt = true;

    try {
      await this.themeService.saveThemeToStorage('auto', accentColor);
    } catch (error) {
      this.logger.warn(
        'AppComponent',
        'maybeShowFirstRunThemePrompt',
        'Failed to cache the undecided System theme',
        { error }
      );
    }
  }

  /**
   * Covers authenticated transitions after app startup, including signup,
   * without allowing a dismissed prompt to reappear in the same session.
   */
  private setupFirstRunThemePromptSubscription(): void {
    this.authService.authSession$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.authService.isAuthenticatedSync()) {
          this.showFirstRunThemePrompt = false;
          this.firstRunThemePromptHandled = false;
          return;
        }

        void this.ensureProfileLoaded().then(() => this.maybeShowFirstRunThemePrompt());
      });
  }

  /** Hide the prompt after either Save or dismiss for the rest of the session. */
  onFirstRunThemeDecided(): void {
    this.firstRunThemePromptHandled = true;
    this.showFirstRunThemePrompt = false;
  }

  /** Re-applies only System/auto themes when the operating-system theme flips. */
  private setupSystemThemeSubscription(): void {
    this.themeService.listenForSystemThemeChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => void this.reapplyAutoThemeForSystemChange());
  }

  private async reapplyAutoThemeForSystemChange(): Promise<void> {
    let mode = this.activeThemeMode;
    let accentColor = this.activeAccentColor;

    try {
      const storedTheme = await this.themeService.loadThemeFromStorage();
      if (storedTheme) {
        mode = storedTheme.mode;
        accentColor = this.normalizeAccentColor(storedTheme.accentColor);
      } else {
        const profile = this.profileService.getCurrentProfile();
        if (profile) {
          mode = profile.HasSelectedTheme === false ? 'auto' : profile.ThemeMode || mode;
          accentColor = this.normalizeAccentColor(profile.AccentColor || accentColor);
        }
      }
    } catch (error) {
      this.logger.warn(
        'AppComponent',
        'reapplyAutoThemeForSystemChange',
        'Failed to read the saved theme while handling an OS theme change',
        { error }
      );
    }

    if (mode === 'auto') {
      this.applyTheme('auto', accentColor);
    }
  }

  closeFullscreen(): void {
    this.presentationService.handleClose();
  }

  private registerActivityListeners(): void {
    this.activityHandler = (event: Event) => this.authService.markActivity(event);
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    document.addEventListener('mousedown', this.activityHandler, opts);
    document.addEventListener('keydown', this.activityHandler, opts);
    document.addEventListener('touchstart', this.activityHandler, opts);
    document.addEventListener('scroll', this.activityHandler, opts);
    document.addEventListener('focusin', this.activityHandler, opts);
    window.addEventListener('focus', this.activityHandler, opts);
  }

  private removeActivityListeners(): void {
    if (!this.activityHandler) return;
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    document.removeEventListener('mousedown', this.activityHandler, opts);
    document.removeEventListener('keydown', this.activityHandler, opts);
    document.removeEventListener('touchstart', this.activityHandler, opts);
    document.removeEventListener('scroll', this.activityHandler, opts);
    document.removeEventListener('focusin', this.activityHandler, opts);
    window.removeEventListener('focus', this.activityHandler, opts);
    this.activityHandler = null;
  }

  private removeVisibilityListener(): void {
    if (!this.visibilityHandler) return;
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.visibilityHandler = null;
  }
}
